import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Avatar,
  InputAdornment,
  CircularProgress,
  Divider,
  Stack,
  Tabs,
  Tab,
  Fade,
  Grow,
} from "@mui/material";
import {
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Security as SecurityIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import Navbar from "./Navbar";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import axios from "axios";

const LoginPageContent = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [signupData, setSignupData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    privateKey: "",
    invite_token: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE;

  useEffect(() => {
    const token = searchParams.get("invite_token");
    const email = searchParams.get("email");
    if (token && email) {
      setSignupData((prev) => ({ ...prev, invite_token: token, email: email }));
      setActiveTab(1); // Switch to the signup tab
    }
  }, [searchParams]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    setError("");
    try {
      const response = await axios.post(
        import.meta.env.VITE_API_GOOGLE_LOGIN,
        { idToken: credentialResponse.credential },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      const result = response.data;
      if (result.success) {
        onLogin(true);
      } else {
        setError(result.message || "Google login failed");
      }
    } catch (error) {
      setError("Google login failed. Please try again.");
      if (error.response && error.response.status >= 500) {
        Sentry.captureException(error, {
          tags: { section: "auth", source: "google-auth" },
        });
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google login failed. Please try again.");
    setGoogleLoading(false);
  };

  const handleTabChange = (e, newValue) => {
    setActiveTab(newValue);
    setError("");
    // Keep invite data if user switches tabs
    const invite_token = signupData.invite_token;
    const email = signupData.invite_token ? signupData.email : "";
    setLoginData({ username: "", password: "" });
    setSignupData({
      username: "",
      email: email,
      password: "",
      confirmPassword: "",
      privateKey: "",
      invite_token: invite_token,
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/login`, loginData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      const result = response.data;
      if (result.success) {
        onLogin(true);
      } else {
        setError(result.message || "Login failed");
      }
    } catch (error) {
      setError("Network error. Please try again.");
      if (error.response && error.response.status >= 500) {
        Sentry.captureException(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/signup`, signupData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      const result = response.data;
      if (result.success) {
        onLogin(true);
      } else {
        setError(result.message || "Signup failed");
      }
    } catch (error) {
      setError(error.response?.data?.message || "Network error. Please try again.");
      if (error.response && error.response.status >= 500) {
        Sentry.captureException(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (setter, field) => (e) => {
    setter((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const isAnyLoading = loading || googleLoading;

  return (
    <>
      <Navbar showLogout={false} companyName="Card Vision" />
      <Box
        sx={{
          minHeight: "100vh",
          pt: 8,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Fade in timeout={800}>
            <Card
              sx={{
                borderRadius: 6,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                overflow: "hidden",
              }}
            >
              <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Avatar
                    sx={{
                      bgcolor: "transparent",
                      background: "linear-gradient(45deg, #667eea, #764ba2)",
                      width: 80, height: 80, mx: "auto", mb: 2,
                      boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
                    }}
                  >
                    {activeTab === 0 ? (
                      <LoginIcon sx={{ fontSize: 40, color: "white" }} />
                    ) : (
                      <PersonAddIcon sx={{ fontSize: 40, color: "white" }} />
                    )}
                  </Avatar>
                  <Typography variant="h4" fontWeight={700}
                    sx={{
                      background: "linear-gradient(45deg, #667eea, #764ba2)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent", mb: 1,
                    }}
                  >
                    Welcome to Card Vision
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {activeTab === 0
                      ? "Welcome back! Please sign in."
                      : "Create your account to get started."}
                  </Typography>
                </Box>

                <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth"
                  sx={{ mb: 4, "& .MuiTab-root": { fontWeight: 600, fontSize: "1rem", py: 2, transition: "all 0.3s ease", },
                    "& .Mui-selected": { color: "#667eea", },
                    "& .MuiTabs-indicator": { backgroundColor: "#667eea", height: 3, borderRadius: "3px 3px 0 0",},
                  }}
                >
                  <Tab label="Sign In" />
                  <Tab label="Sign Up" />
                </Tabs>

                {error && (
                  <Grow in timeout={600}>
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 3, "& .MuiAlert-icon": { alignItems: "center" }, }}>
                      {error}
                    </Alert>
                  </Grow>
                )}

                {signupData.invite_token && activeTab === 1 && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    You've been invited! Complete your registration below.
                  </Alert>
                )}

                <Box sx={{ mb: 4 }}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }} >
                    Quick {activeTab === 0 ? "sign in" : "sign up"} with Google
                  </Typography>
                  {googleLoading ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2, }} >
                      <CircularProgress size={30} sx={{ mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Signing you in...
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} size="large" width="320" text={activeTab === 0 ? "signin_with" : "signup_with"} shape="rectangular" disabled={isAnyLoading} />
                    </Box>
                  )}
                  <Divider sx={{ my: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, backgroundColor: "rgba(255, 255, 255, 0.95)", fontWeight: 500, }} >
                      OR
                    </Typography>
                  </Divider>
                </Box>

                {activeTab === 0 ? (
                  <Fade in timeout={600}>
                    <Box component="form" onSubmit={handleLogin} noValidate>
                      <Stack spacing={3}>
                        <TextField required fullWidth label="Username" placeholder="Enter your username" value={loginData.username} onChange={handleChange(setLoginData, "username")} disabled={isAnyLoading}
                          InputProps={{ startAdornment: ( <InputAdornment position="start"> <PersonIcon sx={{ color: "#667eea" }} /> </InputAdornment> ), }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3, transition: "all 0.3s ease", "&:hover": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.1)", }, "&.Mui-focused": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.2)", }, }, }}
                        />
                        <TextField required fullWidth label="Password" type={showPassword ? "text" : "password"} placeholder="Enter your password" value={loginData.password} onChange={handleChange(setLoginData, "password")} disabled={isAnyLoading}
                          InputProps={{
                            startAdornment: ( <InputAdornment position="start"> <LockIcon sx={{ color: "#667eea" }} /> </InputAdornment> ),
                            endAdornment: ( <InputAdornment position="end"> <Button onClick={() => setShowPassword((p) => !p)} sx={{ minWidth: 0, color: "#667eea", "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.1)", }, }} disabled={isAnyLoading} > {showPassword ? <VisibilityOff /> : <Visibility />} </Button> </InputAdornment> ),
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3, transition: "all 0.3s ease", "&:hover": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.1)", }, "&.Mui-focused": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.2)", }, }, }}
                        />
                        <Button type="submit" fullWidth variant="contained" size="large" disabled={isAnyLoading}
                          sx={{ py: 2, fontSize: "1.1rem", fontWeight: 600, borderRadius: 3, background: "linear-gradient(45deg, #667eea, #764ba2)", boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)", transition: "all 0.3s ease",
                            "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 40px rgba(102, 126, 234, 0.4)", },
                            "&:disabled": { background: "#94a3b8", transform: "none", },
                          }}
                        >
                          {loading ? (<CircularProgress size={24} color="inherit" />) : ("Sign In")}
                        </Button>
                      </Stack>
                    </Box>
                  </Fade>
                ) : (
                  <Fade in timeout={600}>
                    <Box component="form" onSubmit={handleSignup} noValidate>
                      <Stack spacing={3}>
                        <TextField required fullWidth label="Username" placeholder="Choose a username" value={signupData.username} onChange={handleChange(setSignupData, "username")} disabled={isAnyLoading}
                          InputProps={{ startAdornment: ( <InputAdornment position="start"> <PersonIcon sx={{ color: "#667eea" }} /> </InputAdornment> ), }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3, transition: "all 0.3s ease", "&:hover": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.1)", }, "&.Mui-focused": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.2)", }, }, }}
                        />
                        <TextField required fullWidth label="Email" type="email" placeholder="Enter your email address" value={signupData.email} onChange={handleChange(setSignupData, "email")} disabled={isAnyLoading || !!signupData.invite_token}
                          InputProps={{ readOnly: !!signupData.invite_token, startAdornment: ( <InputAdornment position="start"> <EmailIcon sx={{ color: "#667eea" }} /> </InputAdornment> ), }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3, transition: "all 0.3s ease", "&:hover": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.1)", }, "&.Mui-focused": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.2)", }, }, }}
                        />
                        <TextField required fullWidth label="Password" type={showPassword ? "text" : "password"} placeholder="Create a strong password" value={signupData.password} onChange={handleChange(setSignupData, "password")} disabled={isAnyLoading}
                          InputProps={{
                            startAdornment: ( <InputAdornment position="start"> <LockIcon sx={{ color: "#667eea" }} /> </InputAdornment> ),
                            endAdornment: ( <InputAdornment position="end"> <Button onClick={() => setShowPassword((p) => !p)} sx={{ minWidth: 0, color: "#667eea", "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.1)", }, }} disabled={isAnyLoading} > {showPassword ? <VisibilityOff /> : <Visibility />} </Button> </InputAdornment> ),
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3, transition: "all 0.3s ease", "&:hover": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.1)", }, "&.Mui-focused": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.2)", }, }, }}
                        />
                        <TextField required fullWidth label="Confirm Password" type={showConfirmPassword ? "text" : "password"} placeholder="Confirm your password" value={signupData.confirmPassword} onChange={handleChange( setSignupData, "confirmPassword")} disabled={isAnyLoading}
                          InputProps={{
                            startAdornment: ( <InputAdornment position="start"> <LockIcon sx={{ color: "#667eea" }} /> </InputAdornment> ),
                            endAdornment: ( <InputAdornment position="end"> <Button onClick={() => setShowConfirmPassword((p) => !p) } sx={{ minWidth: 0, color: "#667eea", "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.1)", }, }} disabled={isAnyLoading} > {showConfirmPassword ? <VisibilityOff /> : <Visibility />} </Button> </InputAdornment> ),
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3, transition: "all 0.3s ease", "&:hover": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.1)", }, "&.Mui-focused": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.2)", }, }, }}
                        />
                        <TextField required={!signupData.invite_token} fullWidth label="Private Key" type="password" placeholder="Enter the private key" value={signupData.privateKey} onChange={handleChange(setSignupData, "privateKey")} disabled={isAnyLoading} helperText={signupData.invite_token ? "Private key not required for invited users." : ""}
                          InputProps={{ startAdornment: ( <InputAdornment position="start"> <SecurityIcon sx={{ color: "#667eea" }} /> </InputAdornment> ), }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3, transition: "all 0.3s ease", "&:hover": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.1)", }, "&.Mui-focused": { boxShadow: "0 4px 20px rgba(102, 126, 234, 0.2)", }, }, }}
                        />
                        <Button type="submit" fullWidth variant="contained" size="large" disabled={isAnyLoading}
                          sx={{ py: 2, fontSize: "1.1rem", fontWeight: 600, borderRadius: 3, background: "linear-gradient(45deg, #667eea, #764ba2)", boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)", transition: "all 0.3s ease",
                            "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 40px rgba(102, 126, 234, 0.4)", },
                            "&:disabled": { background: "#94a3b8", transform: "none", },
                          }}
                        >
                          {loading ? (<CircularProgress size={24} color="inherit" />) : ("Create Account")}
                        </Button>
                      </Stack>
                    </Box>
                  </Fade>
                )}

                <Box sx={{ backgroundColor: "rgba(102, 126, 234, 0.1)", borderRadius: 3, p: 3, mt: 4, border: "1px solid rgba(102, 126, 234, 0.2)", }} >
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", lineHeight: 1.6, fontSize: "0.9rem", }} >
                    <SecurityIcon sx={{ fontSize: 16, mr: 1, verticalAlign: "middle" }} />
                    Your data is secure and encrypted. We use industry-standard security practices to protect your information.
                  </Typography>
                </Box>
                <Divider sx={{ my: 3 }} />
                <Typography variant="caption" align="center" display="block" color="text.secondary" sx={{ opacity: 0.7 }} >
                  &copy; {new Date().getFullYear()} Asambhav Solutions. All rights reserved.
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>
    </>
  );
};

export const LoginPage = ({ onLogin }) => {
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  if (!GOOGLE_CLIENT_ID) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f5f5f5", }} >
        <Card sx={{ p: 4, maxWidth: 400, textAlign: "center" }}>
          <Typography variant="h6" color="error" gutterBottom>
            Configuration Error
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Google Client ID is missing. Please check your environment variables.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginPageContent onLogin={onLogin} />
    </GoogleOAuthProvider>
  );
};